import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, ScrollView, TouchableOpacity, Text, TouchableHighlight, Platform, ActivityIndicator } from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';
import { v4 as uuidv4 } from 'uuid';
import {loadStripe} from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import { STRIPE_PUBLIC_KEY } from '../../../env';
import { STRIPE_PUBLIC_KEY_STAND } from '../../../env';
import { deleteReservationItem, getReservationDetail, updateReservation, updateReservationItems, sendSMS, sendEmail } from '../../api/Reservation';
import { getHeaderData, getTableData } from '../../api/Price';
import { getDiscountCodesData } from '../../api/Settings';
import { useAlertModal, useConfirmModal } from '../../common/hooks';
import { msgStr } from '../../common/constants/Message';
import BasicLayout from '../../common/components/CustomLayout/BasicLayout';
import { printReservation } from '../../common/utils/Print';
import AddCardModal from '../../common/components/stripe-react/AddCardModal';

import ReservationMainInfo from './ReservationMainInfo';
import { ReservationExtensionPanel } from './ReservationExtensionPanel/ReservationExtensionPanel';
import EquipmentsTable from './EquipmentsTable';
import AddTransactionModal from './ReservationExtensionPanel/AddTransactionModal';
import CommunicationModal from './ReservationExtensionPanel/CommunicationModal';
import AddReservationItemModal from './AddReservationItemModal';
import RefundStripeModal from './ReservationExtensionPanel/RefundStripeModal';
import PenaltyModal from './PenaltyModal';

import { proceedReservationStyle } from './styles/ProceedReservationStyle';
import { getCustomerIdById } from '../../api/Stripe';
import { calculatePricedEquipmentData, getPriceTableByBrandAndDate, calculatePricedEquipmentDataWithVRPrices } from './CalcPrice';
import { useRequestPriceLogicDataQuery } from '../../redux/slices/baseApiSlice';
import { BOHTlbCheckbox } from '../../common/components/bohtoolbar';
import { createPenalty } from '../../api/Penality';

interface Props {
  goBack: () => void;
  reservationId?: string | number;
}

// Defined at module scope so its component identity stays stable across renders.
// Declaring it inside ProceedReservation created a new component function on every
// render, causing React to unmount/remount the whole subtree (and reset child
// state such as the ReservationExtensionPanel's active tab) on each re-render.
const StripeProviderBaseoffPlatform = ({ children, stripePromise }: { children: React.ReactNode; stripePromise: any }) => {
  if (Platform.OS === 'web') {
    return <Elements stripe={stripePromise}>{children}</Elements>;
  }
  return <>{children}</>;
};

// Build a Date from a YYYY-MM-DD date string and an optional time string
// (supports "HH:mm", "HH:mm:ss" and "h:mm AM/PM"). Falls back to local midnight
// when no/invalid time is provided.
const buildDateTime = (dateStr: string, timeStr?: string | null): Date => {
  const baseDate = (() => {
    const m = (dateStr || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    return d;
  })();
  if (!timeStr) return baseDate;
  const tm = String(timeStr).match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i);
  if (!tm) return baseDate;
  let h = parseInt(tm[1], 10);
  const mm = parseInt(tm[2], 10);
  const ss = tm[3] ? parseInt(tm[3], 10) : 0;
  const ampm = tm[4]?.toUpperCase();
  if (ampm === 'PM' && h < 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;
  baseDate.setHours(h, mm, ss, 0);
  return baseDate;
};

export const ProceedReservation = ({ goBack, reservationId }: Props) => {

  const { showAlert } = useAlertModal();
  const { showConfirm } = useConfirmModal();

  const [updateCount, setUpdateCount] = useState<number>(0);
  const [reservationInfo, setReservationInfo] = useState<any>();
  const [isAddTransactionModalVisible, setAddTransactionModalVisible] = useState(false);
  const [headerData, setHeaderData] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [nextStageProcessingStatus, setNextStageProcessingStatus] = useState<boolean>(false);
  const [isRefundStripeModalVisible, setRefundStripeModalVisible] = useState(false);
  const [isPenaltyModalVisible, setPenaltyModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [customerId, setCustomerId] = useState<string|null>(null);
  const { data: priceLogicData } = useRequestPriceLogicDataQuery({}, { refetchOnFocus: true });
  const [price_table_id, setPriceTableId] = useState(null);
  const [priceTableData, setPricetableData] = useState(null);
  const previousReservationInfo = useRef<any>(undefined);
  
  const [refundDetails, setRefundDetails] = useState<any>({
    id: null,
    amount: null,
    payment_intent: null
  });

  const openRefundModal = (refundDetails) => {
    setRefundDetails(refundDetails)
    setRefundStripeModalVisible(true);
  };
  const closeRefundModal = () => {
    setRefundStripeModalVisible(false);
  };

  const openAddTransactionModal = (nextStageProcessingStatus) => {
    setNextStageProcessingStatus(nextStageProcessingStatus);
    setAddTransactionModalVisible(true);
  };
  const closeAddTransactionModal = () => {
    setAddTransactionModalVisible(false);
  };

  const [isAddCardModalVisible, setAddCardModalVisible] = useState(false);
  const openAddCardModal = () => {
    setAddCardModalVisible(true);
  };
  const closeAddCardModal = () => {
    setAddCardModalVisible(false);
  };

  const [equipmentData, setEquipmentData] = useState<Array<any>>([]);
  const [isAddReservationItemModalVisible, setAddReservationItemModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState();
  const [editingIndex, setEditingIndex] = useState();
  const [isCommunicationModalVisible, setCommunicationModalVisible] = useState(false);

  const openCommunicationModal = () => {
    setCommunicationModalVisible(true);
  };
  
  const closeCommunicationModal = () => {
    setCommunicationModalVisible(false);
  };
  
  const closePenaltyModal = () => {
    setPenaltyModalVisible(false);
  };

  const openAddReservationItemModal = () => {
    editReservationItem(null, null);
    setAddReservationItemModalVisible(true);
  };
  const closeAddReservationItemModal = () => {
    setAddReservationItemModalVisible(false);
  };

  const editReservationItem = (item, index) => {
    setAddReservationItemModalVisible(true);
    setEditingItem(item);
    setEditingIndex(index);
  };

  const handlePenalty = (item, index) => {
    setEditingItem(item);
    setEditingIndex(index);
    setPenaltyModalVisible(true);
  };

  const handlePenaltySave = (penaltyData) => {
    const payload = {
      item_id: penaltyData.item?.id || penaltyData.item?.item_id,
      returnDate: penaltyData.penalty.returnDate,
      returnTime: penaltyData.penalty.returnTime,
      milesDriven: penaltyData.penalty.milesDriven,
      returnedBattery: penaltyData.penalty.returnedBattery
    };

    // setPenaltyModalVisible(false);


    // createPenalty(payload, (response, status, error) => {
    //   if (status === 200 && response && !response.error) {
    //     showAlert('success', 'Penalty created successfully');
    //   } else {
    //     showAlert('error', error || 'Failed to create penalty');
    //   }
    // });
  };

  const calcAndSetData = async (ReservationItems:Array<any>, repriceAll: boolean = false) =>{
    let calculatedReservedItems;

    // For same-day rentals, bake the stored times into the dates so the rental
    // duration (diff) reflects the selected hourly slot. Multi-day rentals keep
    // the midnight-to-midnight behaviour so day-based tiers stay correct.
    const isSameDay = reservationInfo?.start_date === reservationInfo?.end_date;
    const hasTimes = !!reservationInfo?.start_time && !!reservationInfo?.end_time;
    const oldStartDate = (isSameDay && hasTimes)
      ? buildDateTime(reservationInfo.start_date, reservationInfo.start_time)
      : new Date(`${reservationInfo.start_date} 00:00:00`);
    const oldEndDate = (isSameDay && hasTimes)
      ? buildDateTime(reservationInfo.end_date, reservationInfo.end_time)
      : new Date(`${reservationInfo.end_date} 00:00:00`);

    if (reservationInfo && reservationInfo.pricing_system === true && reservationInfo.brand_id && reservationInfo.delivery_method !== undefined) {
      try {
        calculatedReservedItems = await calculatePricedEquipmentDataWithVRPrices(
          ReservationItems,
          reservationInfo.brand_id,
          reservationInfo.delivery_method,
          new Date(`${reservationInfo.start_date} 00:00:00`),
          new Date(`${reservationInfo.end_date} 00:00:00`),
          reservationInfo.start_time || null,
          reservationInfo.end_time || null
        );
        console.log('Using new price calculation API with time-based pricing');
      } catch (error) {
        console.error('failed, falling back to old system:', error);
        // Fallback to old system
        calculatedReservedItems = await calculatePricedEquipmentData(
          headerData, 
          price_table_id, 
          priceTableData || [], 
          ReservationItems, 
          oldStartDate, 
          oldEndDate
        );
      }
    } else {
      // Use old system
      console.log('Using old price calculation system');
      calculatedReservedItems = await calculatePricedEquipmentData(
        headerData, 
        price_table_id, 
        priceTableData || [], 
        ReservationItems, 
        oldStartDate, 
        oldEndDate
      );
    }

    // When not re-pricing for a date change, preserve the stored price for
    // unchanged items. This keeps price-logic changes from retroactively
    // changing bike prices while still allowing new/changed families to get
    // fresh calculated prices.
    if (!repriceAll) {
      const originalItems = reservationInfo?.items
        ? (typeof reservationInfo.items === 'string' ? JSON.parse(reservationInfo.items) : reservationInfo.items)
        : [];
      calculatedReservedItems = calculatedReservedItems.map((item: any) => {
        const original = originalItems.find((orig: any) => orig.id === item.id);
        const originalPrice = parseFloat(original?.price ?? 0);
        if (
          original &&
          original.family_id == item.family_id &&
          original.quantity == item.quantity &&
          originalPrice > 0
        ) {
          return { ...item, price: originalPrice };
        }
        return item;
      });
    }

    let prices = {
      subtotal: 0,
      tax: 0,
      discount: 0,
      total: 0,
    }

    calculatedReservedItems.map((item:any)=>{
      let subtotal = item.price || 0;
      prices.subtotal += subtotal;
    });

    if(reservationInfo.promo_code){
      const selectedDiscount:any = discountCodes.find((item:any) => {
          return item.id == reservationInfo.promo_code;
      });

      if(selectedDiscount){
        prices.discount = selectedDiscount.type == 1 ? (Math.round(prices.subtotal * selectedDiscount.amount) / 100) : selectedDiscount.amount;
      }else{
        prices.discount = 0;
      }
    }
    // Recalculate Service Fee and Bodhi Protect from the store settings so the LLC
    // amounts stay in sync whenever items are added, edited or removed.
    // This MUST mirror the customer checkout formula in CustomerReservationProvider
    // so the admin total matches what the customer was actually charged.
    const store = reservationInfo.store || {};
    const driverTip = reservationInfo.driver_tip || 0;
    const deliveryCharge = reservationInfo.delivery_charge || 0;

    let service_fee = 0;
    if (store.is_service_fee_enabled && store.service_fee_percentage) {
      const serviceFeePercentage = parseFloat(store.service_fee_percentage);
      // Calculate fee per item and round, then sum. This ensures fees scale proportionally
      // when items are added (e.g., 1 item = $2.03, 2 items = $4.06, not $4.05).
      service_fee = calculatedReservedItems.reduce((sum: number, item: any) => {
        const itemSubtotal = parseFloat(item.price || 0);
        const itemFee = Math.round(itemSubtotal * (serviceFeePercentage / 100) * 100) / 100;
        return sum + itemFee;
      }, 0);
    }

    let bodhi_protect_price = 0;
    if (store.is_bodhi_protect_enabled && !reservationInfo.bodhi_protect_opted_out && store.bodhi_protect_percentage) {
      const bodhiProtectPercentage = parseFloat(store.bodhi_protect_percentage);
      // Calculate fee per item and round, then sum. This ensures fees scale proportionally.
      bodhi_protect_price = calculatedReservedItems.reduce((sum: number, item: any) => {
        const itemSubtotal = parseFloat(item.price || 0);
        const itemFee = Math.round(itemSubtotal * (bodhiProtectPercentage / 100) * 100) / 100;
        return sum + itemFee;
      }, 0);
    }

    // Tax base includes the service fee (matches customer checkout).
    prices.tax = (prices.subtotal - prices.discount + driverTip + deliveryCharge + service_fee) * (reservationInfo.tax_rate ? reservationInfo.tax_rate / 100 : 0) || 0;
    // Total includes service fee, tax and Bodhi Protect (when not opted out).
    prices.total = prices.subtotal - prices.discount + driverTip + deliveryCharge + service_fee + prices.tax + bodhi_protect_price;

    const payload = {
      id: reservationInfo.id,
      items : calculatedReservedItems,
      subtotal : prices.subtotal,
      tax_amount : prices.tax,
      discount_amount : prices.discount,
      service_fee : service_fee,
      bodhi_protect_price : bodhi_protect_price,
      total_price: prices.total
    }

    updateReservation(payload, (jsonRes, status) => {
      switch (status) {
        case 201:
          setUpdateCount(prev => prev + 1);
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    })
  }

  const addReservationItem = async (productFamily, quantity, extras) => {
    setIsLoading(true);

    const newItem = {
      ...productFamily,
      id: parseInt(uuidv4(), 16),
      reservation_id: reservationInfo.id,
      family_id: productFamily.id,
      price_group_id: productFamily?.lines[0]?.price_group_id ?? 0,
      quantity: 1,
      extras: extras,
    }

    let newData = [...equipmentData];
    if(quantity){
      for(let i=0; i<quantity; i++){
        newData.push(newItem);
      }
    }
    calcAndSetData(newData);
  }

  const updateReservationItem = async (oldFamily, newFamily, quantity, extras) => {
    setIsLoading(true);
    const newItem = {
      ...newFamily,
      id: oldFamily.id,
      reservation_id: oldFamily.reservation_id,
      family_id: newFamily.id,
      price_group_id: newFamily?.lines[0]?.price_group_id ?? 0,
      quantity: 1,
      extras: extras,
    }

    const replaceIndex = editingIndex;
    const newData = equipmentData.map((item, index) => {
      if (index === replaceIndex) {
        return { ...newItem };
      }
      return item;
    });
  
    calcAndSetData(newData);
  }

  const removeReservationItem = async (item, index) => {
    showConfirm(msgStr('deleteConfirmStr'), () => {
      const updatedEquipmentData = [...equipmentData.slice(0, index), ...equipmentData.slice(index + 1)];
      setIsLoading(true);
      deleteReservationItem({id:item.id}, (jsonRes, status)=>{
        if(status == 200){
          calcAndSetData(updatedEquipmentData);
        }
      })
    });
  }

  const confirmNextStage = () =>{
    if(!reservationInfo || !reservationInfo.id) return;

    if(reservationInfo.stage > 3) return;

    if(reservationInfo.paid < reservationInfo.total_price){
      openAddTransactionModal(true);
    }else processNextStage();

    return;
  }

  const CancelOrder = () =>{
    if(!reservationInfo || !reservationInfo.id) return;

    if(reservationInfo.stage > 2) return;

    const payload = {
      id: reservationInfo.id,
      stage: 5,
      printed: true,
    }

    updateReservation(payload, (jsonRes, status) => {
      switch (status) {
        case 201:
          setReservationInfo(prev => {
            return { ...prev, stage: 5, printed: true };
          });
          const ids = equipmentData.map(item => item.id);
          const payloadItems = {
            ids: ids,
            data: { status: 0 }
          }
          updateReservationItems(payloadItems);
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    })
    return;
  }

  const processNextStage = () => {
    const payload = {
      id: reservationInfo.id,
      stage: (reservationInfo.stage + 1),
    }
    
    updateReservation(payload, (jsonRes, status) => {
      switch (status) {
        case 201:
          setReservationInfo(prev => {
            return { ...prev, stage: (prev.stage + 1) };
          });
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
      setNextStageProcessingStatus(false);
    })
  }

  const sendSMSAsync = (data) => {
    return new Promise((resolve) => {
      sendSMS(data, (jsonRes, status) => {
        resolve({ type: 'sms', status, jsonRes });
      });
    });
  };

  const sendEmailAsync = (data) => {
    return new Promise((resolve) => {
      sendEmail(data, (jsonRes, status) => {
        resolve({ type: 'email', status, jsonRes });
      });
    });
  };

  const handleSendCommunication = (method: string, type: string = 'order_details', location?: { latitude: number; longitude: number }) => {
    switch (method) {
      case 'sms':
        sendSMSAsync({
          order_number: reservationInfo.order_number,
          method: 'sms',
          type,
          to: reservationInfo.phone_number,
          // lat: location?.latitude,
          // lng: location?.longitude,
        }).then(({ status, jsonRes }) => {
          if (status === 200) {
            showAlert('success', 'SMS sent successfully');
          } else {
            if (jsonRes?.error) showAlert('error', jsonRes.error);
            else showAlert('error', msgStr('unknownError'));
          }
        });
        break;

      case 'email':
        sendEmailAsync({
          order_number: reservationInfo.order_number,
          method: 'email',
          type,
          to: reservationInfo.email,
          // lat: location?.latitude,
          // lng: location?.longitude,
        }).then(({ status, jsonRes }) => {
          if (status === 200) {
            showAlert('success', 'Email sent successfully');
          } else {
            if (jsonRes?.error) showAlert('error', jsonRes.error);
            else showAlert('error', msgStr('unknownError'));
          }
        });
        break;
      case 'both':
        Promise.allSettled([
          sendSMSAsync({
            order_number: reservationInfo.order_number,
            method: 'sms',
            type,
            to: reservationInfo.phone_number,
            // lat: location?.latitude,
            // lng: location?.longitude,
          }),
          sendEmailAsync({
            order_number: reservationInfo.order_number,
            method: 'email',
            type,
            to: reservationInfo.email,
            // lat: location?.latitude,
            // lng: location?.longitude,
          }),
        ]).then((results) => {
          let allSuccess = true;
          const errors = [];
        
          results.forEach((result: any) => {
            if (result.status === 'fulfilled') {
              const { type, status, jsonRes } = result.value;
              if (status !== 200) {
                allSuccess = false;
                if (jsonRes?.error) {
                  errors.push(`${type.toUpperCase()} error: ${jsonRes.error}`);
                } else {
                  errors.push(`${type.toUpperCase()} error: ${msgStr('unknownError')}`);
                }
              }
            } else {
              allSuccess = false;
              errors.push('Unexpected error occurred while sending communication.');
              console.error(result.reason);
            }
          });
        
          if (allSuccess) {
            showAlert('success', 'Email and SMS sent successfully');
            console.log('Both SMS and Email sent successfully.');
          } else {
            if (errors.length > 0) {
              showAlert('error', errors[0]);
            } else {
              showAlert('error', 'Failed to send notifications');
            }
            console.warn('One or more methods failed.');
          }
        });
        break;
      default:
        break;
    }
  };

  const handleUpdatePrinted = (value: boolean) => {
    updateReservation({
      id: reservationInfo.id,
      printed: value,
    }, (jsonRes, status) => {
      switch (status) {
        case 201:
          setReservationInfo(prev => {
            return { ...prev, printed: value };
          });
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    })
  };

  useEffect(()=>{
    getDiscountCodesData((jsonRes, status, error) => {
      switch (status) {
        case 200:
          setDiscountCodes(jsonRes);
          break;
      }
    });
  }, [])

  useEffect(() => {
    if (!reservationId ) {
      showAlert('error', 'Non valid reservation!');
      goBack();
    }else{
      setIsLoading(true);
      getReservationDetail(reservationId, (jsonRes, status, error) => {
        switch (status) {
          case 200:
            if(jsonRes.total_price == 0){
              jsonRes.total_price = jsonRes.subtotal + jsonRes.tax_amount - jsonRes.discount_amount;
            }
            setReservationInfo(jsonRes);
            setIsLoading(false);
            break;
          case 500:
            setIsLoading(false);
            showAlert('error', msgStr('serverError'));
            goBack();
            break;
          default:
            setIsLoading(false);
            if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
            else showAlert('error', msgStr('unknownError'));
            goBack();
            break;
        }
      });
    }
  }, [reservationId, updateCount]);

  useEffect(() => {
    if(price_table_id){
      getHeaderData(price_table_id, (jsonRes, status, error) => {
        switch (status) {
          case 200:
            setHeaderData(jsonRes);
            break;
          case 500:
            showAlert('error', msgStr('serverError'));
            setHeaderData([]);
            break;
          default:
            if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
            else showAlert('error', msgStr('unknownError'));
            setHeaderData([]);
            break;
        }
      });
      getTableData(price_table_id, (jsonRes:any, status, error) => {
        switch (status) {
          case 200:
            setPricetableData(jsonRes)
            break;
          default:
            setPricetableData(null);
            break;
        }
      });
    }else{
      setHeaderData([]);
      setPricetableData([]);
    } 

  }, [price_table_id]);

  useEffect(() => {
    if(reservationInfo && reservationInfo.start_date){
      const priceTable = getPriceTableByBrandAndDate(priceLogicData, reservationInfo.brand_id, new Date(reservationInfo.start_date));
      setPriceTableId(priceTable?.id??null);
    }
  }, [priceLogicData, (reservationInfo && reservationInfo.brand_id), (reservationInfo && reservationInfo.start_date)])

  useEffect(()=>{
    if(reservationInfo && reservationInfo.stripe_cus_id ){
      setCustomerId(reservationInfo.stripe_cus_id)
    }else if(reservationInfo && reservationInfo.customer_id) {
      getCustomerIdById({id:reservationInfo.customer_id}, (jsonRes, status)=>{
        if(status == 200){
          setCustomerId(jsonRes);

          const payload = {
            id: reservationInfo.id,
            stripe_cus_id : jsonRes,
          }
      
          updateReservation(payload)
        }
        else setCustomerId(null);
      })
    } else setCustomerId(null);
  }, [reservationInfo && reservationInfo.customer_id])

  useEffect(() => {
    if(reservationInfo) {
      if(typeof reservationInfo.items === 'string'){
        setEquipmentData(JSON.parse(reservationInfo.items));
      }else if(typeof reservationInfo.items === 'object') {
        setEquipmentData(reservationInfo.items);
      }
    }
  }, [reservationInfo])

  useEffect(()=>{
    if( headerData.length>0 && price_table_id && priceTableData && reservationInfo.start_date && reservationInfo.end_date && reservationInfo.items){
      const datesChanged = previousReservationInfo.current && (
        reservationInfo.start_date !== previousReservationInfo.current.start_date ||
        reservationInfo.end_date !== previousReservationInfo.current.end_date ||
        reservationInfo.start_time !== previousReservationInfo.current.start_time ||
        reservationInfo.end_time !== previousReservationInfo.current.end_time
      );
      calcAndSetData(reservationInfo.items, !!datesChanged);
      previousReservationInfo.current = reservationInfo;
    }
  }, [
    (reservationInfo && reservationInfo.start_date),
    (reservationInfo && reservationInfo.end_date),
    (reservationInfo && reservationInfo.start_time),
    (reservationInfo && reservationInfo.end_time),
    (reservationInfo && reservationInfo.driver_tip),
    (reservationInfo && reservationInfo.promo_code),
    (reservationInfo && reservationInfo.tax_rate),
  ]);

  const convertStageToString = (stage) => {
    switch (stage) {
      case null: case 'null': return 'Draft';
      case 0: case '0': return 'Draft';
      case 1: case '1': return 'Provisional';
      case 2: case '2': return 'Confirmed';
      case 3: case '3': return 'Checked out';
      case 4: case '4': return 'Checked in';
      case 5: case '5': return 'Canceled';
      default:  return 'Invalid stage';
    }
  }

  const convertStageToBgColor = (stage) => {
    switch (stage) {
      case 1: case '1': return '#262E32';
      case 2: case '2': return '#4379FF';
      case 3: case '3': return '#B8393C';
      case 4: case '4': return '#4CBA70';
      case 5: case '5': return '#666';
      default:  return '#262E32';
    }
  }

  const stripePromise = useMemo(() => {
    if (reservationInfo?.store?.store_name && reservationInfo.store.store_name.toLocaleLowerCase().includes('stand')) {
      console.log('using bike stand key');
      return loadStripe(STRIPE_PUBLIC_KEY_STAND as string);
    } else {
      console.log('using default key');
      return loadStripe(STRIPE_PUBLIC_KEY as string);
    }
  }, [(reservationInfo && reservationInfo.brand_id)]);

  const ReservationMainInfoEl = useMemo(()=>(<ReservationMainInfo details={reservationInfo} setUpdateCount={setUpdateCount}/>), [reservationInfo])

  const EquipmentsTableEl = useMemo(()=>(<EquipmentsTable
    items={equipmentData}
    width={"100%"}
    onEdit={(item, index)=>{
      editReservationItem(item, index);
    }}
    onDelete={(item, index)=>{
      removeReservationItem(item, index);
    }}
    onPenalty={(item, index)=>{
      handlePenalty(item, index);
    }}
    isExtra={true}
    extraWith={350}
  />), [equipmentData])

  return (
    <StripeProviderBaseoffPlatform stripePromise={stripePromise}>
      <BasicLayout
        goBack={()=>{
          goBack();
        }} 
        screenName={'Proceed Reservation'} 
        containerStyle={{
          backgroundColor:'#f7f7f7',
        }}
      >
        <div style={{overflow:'auto', padding:'0 30px'}}>
          <div style={{width:'fit-content', margin:'auto'}}>
            <View style={styles.container}>
              <View style={{flexDirection:'row', zIndex:10}}>
                {ReservationMainInfoEl}
                <ReservationExtensionPanel 
                  reservationInfo={reservationInfo || {}}
                  openAddTransactionModal={()=>openAddTransactionModal(false)}
                  openRefundModal={openRefundModal}
                />
              </View>
              <View style={{flexDirection:'row', justifyContent:'space-between', marginVertical:18}}>
                <View style={{flexDirection:'row', alignItems:'center'}}>
                  <View style={[styles.stageText, {backgroundColor:convertStageToBgColor(reservationInfo?.stage??null)}]}>
                    <View style={[styles.circle, {left:10}]}></View>
                    <View style={[styles.circle, {right:10}]}></View>
                    <Text style={{color:'white', fontWeight:'bold', fontSize:15, fontFamily:'monospace'}}>{convertStageToString(reservationInfo?.stage??null)}</Text>
                  </View>
                  <TouchableOpacity 
                    disabled={(reservationInfo && reservationInfo.stage>3)?true:false}
                    style={[
                      styles.nextStageButton,
                      (reservationInfo && reservationInfo.stage > 3) && { backgroundColor: '#ccc' }
                    ]}
                    onPress={confirmNextStage}>
                    <View style={{flexDirection:'row', alignItems:'center'}}>
                      <Text style={styles.buttonText}>Next</Text>
                      <FontAwesome5 name="angle-right" size={18} color="white" style={{marginLeft:10}}/>
                    </View>
                  </TouchableOpacity>
                  <TouchableOpacity 
                    disabled={(reservationInfo && reservationInfo.stage>2)?true:false}
                    style={[
                      styles.nextStageButton,
                      (reservationInfo && reservationInfo.stage > 2) && { backgroundColor: '#ccc' },
                      {marginLeft:10},
                    ]}
                    onPress={CancelOrder}>
                      <Text style={[styles.buttonText, {marginLeft:0, height:21}]}>Cancel</Text>
                  </TouchableOpacity>
                </View>
                <View style={{flexDirection:'row', alignItems:'center'}}>
                  <BOHTlbCheckbox
                    label={'Printed'}
                    CheckboxProps={{
                      value:reservationInfo?.printed
                    }}
                    onPress={()=>{
                      handleUpdatePrinted(!reservationInfo?.printed);
                    }}
                  />
                  <TouchableOpacity style={styles.outLineButton} onPress={()=>printReservation(reservationInfo.id)}>
                    <Text style={styles.outlineBtnText}>Print</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.outLineButton} onPress={openCommunicationModal}>
                    <Text style={styles.outlineBtnText}>Resend</Text>
                  </TouchableOpacity>
                  {/* <TouchableOpacity style={[styles.outLineButton, {borderColor: '#4379FF'}]} onPress={openAddCardModal}>
                    <Text style={[styles.outlineBtnText, {color:'#4379FF'}]}>Stripe</Text>
                  </TouchableOpacity> */}
                  <TouchableOpacity style={[styles.outLineButton, {borderColor:'#DC3545'}]}>
                    <View style={{flexDirection:'row', alignItems:'center'}}>
                      <FontAwesome5 name={'bookmark'} size={18} color="#DC3545" style={{marginRight:10, marginTop:1}}/>
                      <Text style={[styles.outlineBtnText, {color:'#DC3545'}]}>Add</Text>
                    </View>
                  </TouchableOpacity>
                  <TouchableOpacity style={[styles.outLineButton, {borderColor:'#DC3545'}]} onPress={()=>openAddTransactionModal(false)}>
                    <Text style={[styles.outlineBtnText, {color:'#DC3545'}]}>Add transaction</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.outLineButton}>
                    <Text style={styles.outlineBtnText}>More</Text>
                  </TouchableOpacity>
                </View>
              </View>
              <View style={[styles.reservationRow, {justifyContent:'flex-end'}]}>
                <TouchableHighlight 
                  style={[styles.addItemButton]} 
                  onPress={openAddReservationItemModal}>
                  <View style={{flexDirection:'row', alignItems:'center'}}>
                    <FontAwesome5 name="plus" size={14} color="white" style={{marginTop:3}}/>
                    <Text style={styles.buttonText}>Add Items</Text>
                  </View>
                </TouchableHighlight>
              </View>
              <View>
                {EquipmentsTableEl}
              </View>
            </View>
          </div>
        </div>
        <AddTransactionModal
          isModalVisible={isAddTransactionModalVisible}
          nextStageProcessingStatus={nextStageProcessingStatus}
          customerId={customerId}
          reservationInfo = {reservationInfo}
          addCard={openAddCardModal}
          closeModal={closeAddTransactionModal}
          onAdded={(nextStageProcessingStatus)=>{
            if(nextStageProcessingStatus) processNextStage();
            setUpdateCount(prev => prev + 1);
            closeAddTransactionModal();
          }}
          continueWithouProcessing={()=>{
            if(nextStageProcessingStatus) processNextStage();
            setUpdateCount(prev => prev + 1);
            closeAddTransactionModal();
          }}
        />
        <AddReservationItemModal
          isModalVisible={isAddReservationItemModalVisible}
          closeModal={closeAddReservationItemModal}
          item={editingItem}
          onAdded={(productFamily, quantity, extras)=>{
            if (productFamily) {
              addReservationItem(productFamily, quantity, extras);
            }
          }}
          onUpdated={(oldFamily, newFamily, quantity, extras)=>{
            if (newFamily) {
              updateReservationItem(oldFamily, newFamily, quantity, extras);
            }
            editReservationItem(null, null);
          }}
          isExtra={true}
          pricingContext={
            reservationInfo && reservationInfo.pricing_system === true && reservationInfo.brand_id && reservationInfo.start_date && reservationInfo.end_date
              ? {
                  pricingSystem: true,
                  brandId: reservationInfo.brand_id,
                  deliveryMethod: reservationInfo.delivery_method,
                  startDate: reservationInfo.start_date,
                  endDate: reservationInfo.end_date,
                  startTime: reservationInfo.start_time || null,
                  endTime: reservationInfo.end_time || null,
                }
              : undefined
          }
        />
        <RefundStripeModal
          isModalVisible={isRefundStripeModalVisible}
          closeModal={closeRefundModal}
          refundDetails={refundDetails}
          onRefunded={() => setUpdateCount(prev => prev + 1)}
        />
        <CommunicationModal
          isModalVisible={isCommunicationModalVisible}
          closeModal={closeCommunicationModal}
          isDelivered={reservationInfo?.stage >= 3}
          onSend={handleSendCommunication}
        />
        {Platform.OS === 'web' && (
          <AddCardModal
            isModalVisible={isAddCardModalVisible}
            customerId = {customerId}
            reservationId={reservationInfo?.id??null}
            closeModal={closeAddCardModal}
          />
        )}
        <PenaltyModal
          isModalVisible={isPenaltyModalVisible}
          closeModal={closePenaltyModal}
          onSave={handlePenaltySave}
          item={editingItem}
          index={editingIndex}
        />
        {isLoading && (
          <View style={styles.overlay}>
            <ActivityIndicator size="large" color="#0000ff" />
          </View>
        )}
      </BasicLayout>
    </StripeProviderBaseoffPlatform>
  );
};

const styles = proceedReservationStyle
